import { useEffect, useState } from "react";
import { Col, EmptyState, Form, FormInstance, Heading, Icon, Icons, Row, Select, SelectOption, Textarea, useToast } from "@ir/ui";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { BackupRequestLookupsDTO } from "@/core/dto/deduction/remittance/backupRequest/backupRequest.dto";
import { BackupRequestRepository } from "@/infrastructure/repositories/remittance/backupRequest/BackupRequest.repository";
import { useUser } from "@/hooks";
import { BackupRequestContact, BackupRequestType } from "@/core/entities/deduction/remittance/BackupRequestLookups";
import { GetBackupRequestUseCase } from "@/core/usecases/remittance/requestBackup/GetBackupRequest.useCase";
import { BackupRequestDotFoodsFormValues, BackupRequestDataForSubmit } from "./BackupRequestInterface";
import { constructNote } from "./BackupRequestUtils";
import ContactInfo from "../../../ContactInfoForm";
import { createLogger } from "@/infrastructure/services/logger";

const client = GraphQlAdapter.getInstance().getClient();

const mapContactsToOptions = (contacts: BackupRequestContact[]): SelectOption[] =>
  contacts.map(contact => ({
    label: `${contact.name} - ${contact.email}`,
    value: contact.email,
  }));

interface BackupRequestDotFoodsFormProps {
  backupRequestForm: FormInstance<BackupRequestDotFoodsFormValues>;
  onBackupRequestSubmit?: (backupSubmitData: BackupRequestDataForSubmit) => void;
}

const BackupRequestDotFoodsForm = ({ backupRequestForm, onBackupRequestSubmit }: BackupRequestDotFoodsFormProps) => {
  const logger = createLogger("BackupRequestDotFoodsForm");
  const { drawerInfo } = useDeductionDrawer();
  const { user } = useUser();
  const { error: errorToast } = useToast();
  const [backupRequestTypes, setBackupRequestTypes] = useState<BackupRequestType[]>();
  const [disputeContactInfoList, setDisputeContactInfoList] = useState<SelectOption[]>();
  const [isDisputeTypesLoading, setIsDisputeTypesLoading] = useState(false);
  const [noteTemplate, setNoteTemplate] = useState<string>("");

  // Issue Descriptions - Children of selected category
  const getIssueDescriptions = (categoryId: number | null): SelectOption[] => {
    if (!categoryId || !backupRequestTypes || !Array.isArray(backupRequestTypes)) {
      return [];
    }
    const category = backupRequestTypes.find(cat => cat.disputeTypeId === categoryId);
    return (
      category?.children?.map(child => ({
        label: child.disputeTypeName,
        value: child.disputeTypeId?.toString() ?? "",
      })) ?? []
    );
  };

  useEffect(() => {
    const fetchBackupRequestConfig = async () => {
      try {
        setIsDisputeTypesLoading(true);
        if (drawerInfo?.remittanceItemId) {
          const repository = new BackupRequestRepository(client);
          const useCase = new GetBackupRequestUseCase(repository);
          const response = await useCase.execute(
            new BackupRequestLookupsDTO({
              purchaserKey: drawerInfo?.purchaserKey ?? "",
            })
          );

          setBackupRequestTypes(response.disputeTypes.map(type => new BackupRequestType(type)) ?? []);
          setDisputeContactInfoList(mapContactsToOptions(response.contacts?.map(contact => new BackupRequestContact(contact)) ?? []));
          const note = constructNote(response.noteTemplate ?? "", [drawerInfo?.invoiceNumber ?? ""]);
          setNoteTemplate(note);

          // Check if the 'note' field is already populated by the user before setting it
          const currentNoteValue = backupRequestForm.getFieldValue("note");
          if (!currentNoteValue || currentNoteValue === noteTemplate) {
            backupRequestForm.setFieldsValue({
              note: note,
            });
          }
          if (response.contacts?.length === 1) {
            backupRequestForm.setFieldsValue({
              contactName: response.contacts?.[0].name,
              contactEmail: response.contacts?.[0].email,
              contactInfo: {
                label: `${response.contacts?.[0].name} - ${response.contacts?.[0].email}`,
                value: response.contacts?.[0].email,
              },
            });
          }
        }
      } catch (error) {
        logger.error("Error fetching request backup data", error);
        errorToast({
          content: "Failed to fetch request backup data",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsDisputeTypesLoading(false);
      }
    };

    fetchBackupRequestConfig();
  }, [backupRequestForm, errorToast, drawerInfo?.remittanceItemId, drawerInfo?.purchaserKey, noteTemplate, drawerInfo?.invoiceNumber]);

  useEffect(() => {
    if (!backupRequestForm.getFieldValue("contactName") && user?.fullName) {
      backupRequestForm.setFieldValue("contactName", user.fullName);
    }

    if (!backupRequestForm.getFieldValue("contactEmail") && user?.email) {
      backupRequestForm.setFieldValue("contactEmail", user.email);
    }
  }, [backupRequestForm, user]);

  const handleDisputeTypeChange = () => {
    const currentDisputeType = backupRequestForm.getFieldValue("disputeType");
    if (!currentDisputeType) {
      return;
    }
    const issues = getIssueDescriptions(Number(currentDisputeType.value));
    if (issues.length === 1) {
      backupRequestForm.setFieldValue("disputeSubType", { label: issues[0].label, value: issues[0].value });
    } else {
      backupRequestForm.setFieldValue("disputeSubType", undefined);
    }
  };

  const handleValuesChange = (changedValues: Partial<BackupRequestDotFoodsFormValues>) => {
    if (changedValues.disputeType) {
      handleDisputeTypeChange();
    }
  };

  const handleSubmit = (values: BackupRequestDotFoodsFormValues) => {
    const disputeConfig = backupRequestTypes?.at(0);
    const backupSubmitData: BackupRequestDataForSubmit = {
      remittanceItemId: drawerInfo?.remittanceItemId ?? 0,
      claimNumber: drawerInfo?.invoiceNumber ?? "",
      disputeTypeId: Number(disputeConfig?.disputeTypeId),
      disputeSubTypeId: disputeConfig?.children ? Number(disputeConfig?.children?.at(0)?.disputeTypeId) : undefined,
      note: values.note,
      isCustomerDeduction: disputeConfig?.isCustomerDeduction ?? undefined,
      contactInfo:
        values.contactInfo && disputeContactInfoList && disputeContactInfoList.length > 0
          ? // if contact info is in the list
            {
              name: values.contactInfo.label.split("-")[0].trim(),
              email: values.contactInfo.value.trim(),
            }
          : // if contact info is not in the list
            {
              name: values.contactName ?? "",
              email: values.contactEmail ?? "",
            },
    };
    onBackupRequestSubmit?.(backupSubmitData);
  };

  if (!backupRequestForm) {
    return (
      <EmptyState
        className="bg-white border-1 border-color-grey-25 px-4 py-5 m-2"
        icon={Icons.Settings}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-3">
            Loading...
          </Heading>
        }
      />
    );
  }

  return (
    <Form form={backupRequestForm} onFinish={handleSubmit} onValuesChange={handleValuesChange}>
      <Form.Item
        name="note"
        rules={[
          { required: true, message: "Notes are required." },
          { max: 500, message: "Notes must be less than 500 characters." },
          { whitespace: true, message: "Notes cannot contain leading or trailing spaces." },
        ]}
        label="Notes"
        className="mt-3"
      >
        <Textarea minRows={3} showCharCount maxLength={500} placeholder={isDisputeTypesLoading ? "Notes are loading..." : "Enter notes..."} />
      </Form.Item>

      {disputeContactInfoList && disputeContactInfoList.length > 0 && (
        <Row>
          <Col span={12}>
            <Form.Item
              name="contactInfo"
              rules={[{ required: true, message: "Contact Info is required." }]}
              label="Contact Info"
              onMetaChange={() => {
                const currentContactInfo = backupRequestForm.getFieldValue("contactInfo");

                if (currentContactInfo) {
                  backupRequestForm.setFieldValue("contactName", currentContactInfo.label.split("-")[0].trim());
                  backupRequestForm.setFieldValue("contactEmail", currentContactInfo.value.trim());
                }
              }}
            >
              <Select
                maxMenuHeight={150}
                className="select-field"
                placeholder="Select"
                options={disputeContactInfoList}
                isClearable={false}
                isLoading={isDisputeTypesLoading}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {!isDisputeTypesLoading && disputeContactInfoList?.length === 0 && <ContactInfo nameLabel="Contact Name" emailLabel="Contact Email" />}
    </Form>
  );
};

export default BackupRequestDotFoodsForm;
